import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for file upload preview with size validation.
 *
 * Usage:
 *   <div data-controller="dae20--file-upload-preview"
 *        data-dae20--file-upload-preview-max-size-value="5242880"
 *        data-dae20--file-upload-preview-max-files-value="10">
 *     <input type="file" multiple
 *            data-dae20--file-upload-preview-target="input"
 *            data-action="change->dae20--file-upload-preview#filesChanged">
 *     <div data-dae20--file-upload-preview-target="preview"></div>
 *     <div data-dae20--file-upload-preview-target="error"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["input", "preview", "error"]
  static values = {
    maxSize: { type: Number, default: 5242880 },   // 5 MB
    maxFiles: { type: Number, default: 10 }
  }

  connect() {
    this.selectedFiles = new DataTransfer()
  }

  filesChanged() {
    if (!this.hasInputTarget) return

    const newFiles = Array.from(this.inputTarget.files)
    const errors = []

    for (const file of newFiles) {
      if (file.size > this.maxSizeValue) {
        const sizeMB = (file.size / 1048576).toFixed(1)
        const limitMB = (this.maxSizeValue / 1048576).toFixed(0)
        errors.push(`${file.name} (${sizeMB} MB) — максимум ${limitMB} MB`)
        continue
      }

      if (this.selectedFiles.files.length >= this.maxFilesValue) {
        errors.push(`Максимум ${this.maxFilesValue} файлов`)
        break
      }

      this.selectedFiles.items.add(file)
    }

    // Update the real input with accumulated files
    this.inputTarget.files = this.selectedFiles.files

    this.renderPreview()
    this.renderErrors(errors)
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    const dt = new DataTransfer()

    for (let i = 0; i < this.selectedFiles.files.length; i++) {
      if (i !== index) dt.items.add(this.selectedFiles.files[i])
    }

    this.selectedFiles = dt
    this.inputTarget.files = this.selectedFiles.files
    this.renderPreview()
  }

  renderPreview() {
    if (!this.hasPreviewTarget) return

    const files = Array.from(this.selectedFiles.files)

    if (files.length === 0) {
      this.previewTarget.innerHTML = ""
      return
    }

    const items = files.map((file, index) => {
      const sizeMB = (file.size / 1048576).toFixed(2)
      const isImage = file.type.startsWith("image/")
      const thumbnailHtml = isImage
        ? `<img src="${URL.createObjectURL(file)}" class="w-10 h-10 rounded-lg object-cover border border-gray-200" alt="${this.escapeHtml(file.name)}">`
        : `<div class="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400">
             <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
             </svg>
           </div>`

      return `
        <div class="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-gray-200 group">
          ${thumbnailHtml}
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-700 truncate">${this.escapeHtml(file.name)}</p>
            <p class="text-xs text-gray-400">${sizeMB} MB</p>
          </div>
          <button type="button"
                  data-index="${index}"
                  data-action="click->dae20--file-upload-preview#removeFile"
                  class="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  title="Удалить">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      `
    }).join("")

    this.previewTarget.innerHTML = `
      <div class="mt-3 space-y-2">
        <p class="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Выбрано файлов: ${files.length}
        </p>
        ${items}
      </div>
    `
  }

  renderErrors(errors) {
    if (!this.hasErrorTarget) return

    if (errors.length === 0) {
      this.errorTarget.innerHTML = ""
      return
    }

    const items = errors.map(e => `<li>${this.escapeHtml(e)}</li>`).join("")
    this.errorTarget.innerHTML = `
      <div class="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <p class="font-semibold">Некоторые файлы не добавлены:</p>
        <ul class="mt-1 list-disc list-inside">${items}</ul>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
